import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  useDropzone,
  type FileError,
  type FileRejection,
} from 'react-dropzone';

import { MediaWriteService } from '@kit/shared/storage';

interface FileWithPreview extends File {
  preview?: string;
  errors: readonly FileError[];
}

/**
 * Translate storage error messages to user-friendly messages
 */
function getUploadErrorMessage(errorMessage: string): string {
  const lowered = errorMessage.toLowerCase();

  // RLS policy violations
  if (
    lowered.includes('row-level security') ||
    lowered.includes('policy') ||
    lowered.includes('permission denied')
  ) {
    return 'Upload failed. Please try again or refresh the page.';
  }

  // Auth errors
  if (lowered.includes('jwt') || lowered.includes('token')) {
    return 'Session expired. Please refresh the page and try again.';
  }

  // File size errors
  if (lowered.includes('too large') || lowered.includes('size')) {
    return 'File is too large. Please choose a smaller file.';
  }

  // Bucket not found
  if (lowered.includes('bucket') && lowered.includes('not found')) {
    return 'Upload service unavailable. Please try again later.';
  }

  // Network errors
  if (lowered.includes('network') || lowered.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }

  // Default: return original but simplified
  return errorMessage.length > 100
    ? 'Upload failed. Please try again.'
    : errorMessage;
}

type UseSupabaseUploadOptions = {
  /**
   * Name of bucket to upload files to in your Supabase project
   */
  bucketName: string;
  /**
   * Folder to upload files to in the specified bucket within your Supabase project.
   *
   * Defaults to uploading files to the root of the bucket
   *
   * e.g If specified path is `test`, your file will be uploaded as `test/file_name`
   */
  path?: string;
  /**
   * Allowed MIME types for each file upload (e.g `image/png`, `text/html`, etc). Wildcards are also supported (e.g `image/*`).
   *
   * Defaults to allowing uploading of all MIME types.
   */
  allowedMimeTypes?: string[];
  /**
   * Maximum upload size of each file allowed in bytes. (e.g 1000 bytes = 1 KB)
   */
  maxFileSize?: number;
  /**
   * Maximum number of files allowed per upload.
   */
  maxFiles?: number;
  /**
   * The number of seconds the asset is cached in the browser and in the Supabase CDN.
   *
   * This is set in the Cache-Control: max-age=<seconds> header. Defaults to 3600 seconds.
   */
  cacheControl?: number;
  /**
   * When set to true, the file is overwritten if it exists.
   *
   * When set to false, an error is thrown if the object already exists. Defaults to `false`
   */
  upsert?: boolean;
  /**
   * Supabase client to use for the upload.
   */
  client: SupabaseClient;
  /**
   * Callback to call when the upload is successful.
   */
  onUploadSuccess?: (files: string[]) => void;
  /**
   * When set to true, files will automatically upload when dropped/selected.
   * Defaults to `true`.
   */
  autoUpload?: boolean;
  /**
   * Callback when upload errors occur. Receives the error details for
   * component-specific handling (e.g. Sentry capture, custom UI).
   */
  onError?: (errors: { name: string; message: string }[]) => void;
  /**
   * When true, clicking the dropzone area does not open the file picker.
   * Defaults to `true` for desktop drag-and-drop UX. Set to `false` on
   * mobile-oriented uploaders where the entire area should be tappable.
   */
  noClick?: boolean;
};

export type UseSupabaseUploadReturn = ReturnType<typeof useSupabaseUpload>;

/**
 * Hook to upload files to a Supabase bucket.
 *
 * @param options - Options for the upload.
 * @returns The upload state.
 */
export const useSupabaseUpload = (options: UseSupabaseUploadOptions) => {
  const {
    bucketName,
    path,
    allowedMimeTypes = [],
    maxFileSize = Number.POSITIVE_INFINITY,
    maxFiles = 1,
    cacheControl = 3600,
    upsert = false,
    client,
    onUploadSuccess,
    autoUpload = true,
    onError,
    noClick = true,
  } = options;

  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ name: string; message: string }[]>([]);
  const [successes, setSuccesses] = useState<string[]>([]);

  const isSuccess = useMemo(() => {
    if (errors.length === 0 && successes.length === 0) {
      return false;
    }

    if (errors.length === 0 && successes.length === files.length) {
      return true;
    }

    return false;
  }, [errors.length, successes.length, files.length]);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const validFiles = acceptedFiles
        .filter((file) => !files.find((x) => x.name === file.name))
        .map((file) => {
          (file as FileWithPreview).preview = URL.createObjectURL(file);
          (file as FileWithPreview).errors = [];

          return file as FileWithPreview;
        });

      const invalidFiles = fileRejections.map(({ file, errors }) => {
        (file as FileWithPreview).preview = URL.createObjectURL(file);
        (file as FileWithPreview).errors = errors;

        return file as FileWithPreview;
      });

      const newFiles = [...files, ...validFiles, ...invalidFiles];

      setFiles(newFiles);
    },
    [files, setFiles],
  );

  const dropzoneProps = useDropzone({
    onDrop,
    noClick,
    accept: allowedMimeTypes.reduce(
      (acc, type) => ({ ...acc, [type]: [] }),
      {},
    ),
    maxSize: maxFileSize,
    maxFiles: maxFiles,
    multiple: maxFiles !== 1,
    onDragEnter: undefined,
    onDragLeave: undefined,
    onDragOver: undefined,
  });

  const onUpload = useCallback(async () => {
    setLoading(true);

    // [Joshen] This is to support handling partial successes
    // If any files didn't upload for any reason, hitting "Upload" again will only upload the files that had errors
    const filesWithErrors = errors.map((x) => x.name);

    const filesToUpload =
      filesWithErrors.length > 0
        ? [
            ...files.filter((f) => filesWithErrors.includes(f.name)),
            ...files.filter((f) => !successes.includes(f.name)),
          ]
        : files;

    const responses = await Promise.all(
      filesToUpload.map(async (file) => {
        const filePath = path ? `${path}/${file.name}` : file.name;

        const uploadResult = await new MediaWriteService(client).uploadObject(
          bucketName,
          filePath,
          file,
          {
            cacheControl: cacheControl.toString(),
            upsert,
          },
        );

        const fullFilePath = [bucketName, filePath].join('/');

        if (uploadResult.isFailure) {
          return {
            name: file.name,
            message: getUploadErrorMessage(uploadResult.getError().message),
            fullFilePath,
          };
        } else {
          return { name: file.name, message: undefined, fullFilePath };
        }
      }),
    );

    const responseErrors = responses.filter((x) => x.message !== undefined);

    // if there were errors previously, this function tried to upload the files again so we should clear/overwrite the existing errors.
    setErrors(responseErrors);

    if (responseErrors.length > 0) {
      onError?.(responseErrors);
    }

    const responseSuccesses = responses.filter((x) => x.message === undefined);

    const newSuccesses = Array.from(
      new Set([...successes, ...responseSuccesses.map((x) => x.name)]),
    );

    setSuccesses(newSuccesses);

    if (responseSuccesses.length > 0) {
      const files = responseSuccesses.map((item) => {
        return item.fullFilePath;
      });

      onUploadSuccess?.(files);
    }

    setLoading(false);
  }, [
    bucketName,
    cacheControl,
    client,
    errors,
    files,
    onError,
    onUploadSuccess,
    setLoading,
    setErrors,
    setSuccesses,
    path,
    successes,
    upsert,
  ]);

  useEffect(() => {
    if (files.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setErrors([]);
    }

    // If the number of files doesn't exceed the maxFiles parameter, remove the error 'Too many files' from each file
    if (files.length <= maxFiles) {
      let changed = false;

      const newFiles = files.map((file) => {
        if (file.errors.some((e) => e.code === 'too-many-files')) {
          file.errors = file.errors.filter((e) => e.code !== 'too-many-files');
          changed = true;
        }
        return file;
      });

      if (changed) {
        setFiles(newFiles);
      }
    }
  }, [files.length, setFiles, maxFiles, files]);

  // Auto-upload when files are added (if autoUpload is enabled).
  // CRITICAL: must also exclude files that errored on the server (`errors`
  // state). Without that exclusion a single 400 from Supabase Storage
  // causes the effect to re-fire onUpload() indefinitely (errors flips →
  // onUpload identity changes → effect re-runs → uploads same file →
  // 400 again → loop). The dropzone-level `file.errors` only covers
  // client-side validation rejections, not server responses.
  useEffect(() => {
    if (!autoUpload) return;
    if (files.length === 0) return;
    if (loading) return;
    if (isSuccess) return;

    const erroredNames = new Set(errors.map((e) => e.name));

    const hasValidFilesToUpload = files.some(
      (file) =>
        file.errors.length === 0 &&
        !successes.includes(file.name) &&
        !erroredNames.has(file.name),
    );

    if (hasValidFilesToUpload) {
      // Defer to avoid synchronous setState within effect body
      queueMicrotask(() => onUpload());
    }
  }, [autoUpload, files, loading, isSuccess, successes, errors, onUpload]);

  return {
    files,
    setFiles,
    successes,
    isSuccess,
    loading,
    errors,
    setErrors,
    onUpload,
    maxFileSize: maxFileSize,
    maxFiles: maxFiles,
    allowedMimeTypes,
    ...dropzoneProps,
  };
};
